// frontend/src/components/layout/AdminLayout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  FiGrid,
  FiPackage,
  FiShoppingBag,
  FiUsers,
  FiTag,
  FiLogOut,
  FiMenu,
  FiX,
  FiChevronLeft,
  FiBell
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: FiGrid, exact: true },
  { path: '/admin/produits', label: 'Produits', icon: FiPackage },
  { path: '/admin/commandes', label: 'Commandes', icon: FiShoppingBag },
  { path: '/admin/utilisateurs', label: 'Utilisateurs', icon: FiUsers },
  { path: '/admin/categories', label: 'Catégories', icon: FiTag },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const currentTitle =
    navItems.find(item => (item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)))?.label ||
    'Dashboard';

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-[#0a0a0a] overflow-hidden text-gray-900 dark:text-white">
      {/* SIDEBAR */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-white dark:bg-[#111111] border-r border-gray-200 dark:border-[#1f1f1f] flex flex-col transition-all duration-300 shadow-xl lg:shadow-none
          lg:static lg:translate-x-0
          ${sidebarOpen ? 'w-60 translate-x-0' : '-translate-x-full lg:w-16 lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center border-b border-gray-200 dark:border-[#1f1f1f] px-4 gap-3">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
            SM
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ${
              sidebarOpen
                ? 'opacity-100 w-auto'
                : 'lg:opacity-0 lg:w-0 opacity-0 w-0'
            }`}
          >
            <p className="font-bold text-gray-900 dark:text-white text-sm whitespace-nowrap">
              SunuMarket
            </p>

            <p className="text-xs text-primary-600 dark:text-primary-500 whitespace-nowrap">
              Administration
            </p>
          </div>

          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                  active
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f]'
                }`}
                title={!sidebarOpen ? item.label : ''}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />

                <span
                  className={`text-sm font-medium whitespace-nowrap overflow-hidden transition-all duration-300 ${
                    sidebarOpen
                      ? 'opacity-100 w-auto'
                      : 'lg:opacity-0 lg:w-0 opacity-0 w-0'
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-gray-200 dark:border-[#1f1f1f]">
          <div
            className={`flex items-center gap-3 px-3 py-2 mb-2 overflow-hidden transition-all duration-300 ${
              sidebarOpen
                ? 'opacity-100 max-h-20'
                : 'lg:opacity-0 lg:max-h-0 opacity-0 max-h-0'
            }`}
          >
            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase()}
            </div>

            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.name}
              </p>

              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>

          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
          >
            <FiChevronLeft className="w-4 h-4 flex-shrink-0" />

            <span
              className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${
                sidebarOpen
                  ? 'opacity-100 w-auto'
                  : 'lg:opacity-0 lg:w-0 opacity-0 w-0'
              }`}
            >
              Voir la boutique
            </span>
          </Link>

          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
          >
            <FiLogOut className="w-4 h-4 flex-shrink-0" />

            <span
              className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${
                sidebarOpen
                  ? 'opacity-100 w-auto'
                  : 'lg:opacity-0 lg:w-0 opacity-0 w-0'
              }`}
            >
              Déconnexion
            </span>
          </button>
        </div>
      </aside>

      {/* BACKDROP MOBILE */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white dark:bg-[#111111] border-b border-gray-200 dark:border-[#1f1f1f] flex items-center justify-between px-4 sm:px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Hamburger mobile */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
            >
              <FiMenu className="w-5 h-5" />
            </button>

            {/* Toggle desktop */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:block p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors"
            >
              {sidebarOpen ? (
                <FiX className="w-5 h-5" />
              ) : (
                <FiMenu className="w-5 h-5" />
              )}
            </button>

            <h1 className="text-lg font-bold text-gray-900 dark:text-white lg:hidden">
              {currentTitle}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <button className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1f1f1f] rounded-xl transition-colors">
              <FiBell className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 dark:bg-[#0a0a0a]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}